// frontend/src/pages/AssignPoints/RosterSelectPage.js
// Lists rosters (a.k.a. groups) for the logged-in educator.
// Robust: tries multiple endpoints, and *catches* 404s so we can fall back.
//
// Refactored backend preferred endpoints:
//   GET /api/groups/me/          (educator's groups/rosters)
//   GET /api/rosters/            (fallback)
//
// After selecting a roster, shows student checkboxes (with "Select All").
// On Continue → /assign/reasons?roster_id=<id>&roster_name=<name>&...
//   and passes { state: { studentIds: [...] } }

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../api/client";
import { studentsApi } from "../../api/studentsApi";

// IMPORTANT: This file only exports RosterSelectPage.
// Do NOT re-export this as StudentSelectPage anywhere.

function pluckArray(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.groups))  return payload.groups;
  if (Array.isArray(payload.data))    return payload.data;
  if (Array.isArray(payload))         return payload;
  return [];
}

function is2xx(res) {
  return res && typeof res.status === "number" && res.status >= 200 && res.status < 300;
}

async function tryEndpoints() {
  const candidates = [
    // Refactored backend: educator's rosters
    "/api/groups/me/",
    "/api/groups/me", // fallback if deployed without slash

    // Fallbacks
    "/api/rosters/",
    "/api/rosters",

    // Extra legacy candidates (keep them last; harmless but slower)
    "/api/groups/",
    "/api/groups",
    "/api/me/groups",
    "/api/teachers/me/groups",
    "/api/educators/me/groups",
    "/api/educator/groups",
  ];

  const attempts = [];
  for (const url of candidates) {
    try {
      const res = await api.get(url);
      const ok = is2xx(res);
      attempts.push({ url, status: res?.status ?? "?", ok });
      if (ok) {
        const list = pluckArray(res.data) || [];
        return { ok: true, list, hit: url, attempts };
      }
    } catch (e) {
      const status = (e && e.status) || (e && e.response && e.response.status) || "ERR";
      attempts.push({ url, status, ok: false, error: String(e?.message || e) });
      // continue trying next endpoint
    }
  }
  return { ok: false, list: [], hit: null, attempts };
}

// ---------- Roster student loading (robust across backends) ----------

async function fetchRosterStudentsRobust(rosterId) {
  if (!rosterId) return [];

  // Refactored backend supports:
  //   GET /api/rosters/<id>/students/
  // Try common endpoints that may return either an array, {results:[]}, or {students:[]}
  const tries = [
    `/api/rosters/${rosterId}/students/`,
    `/api/rosters/${rosterId}/students`, // fallback if deployed without slash
    `/api/groups/${rosterId}/students/`,
    `/api/groups/${rosterId}/students`,
    `/api/rosters/${rosterId}/`,
    `/api/rosters/${rosterId}`,
    `/api/groups/${rosterId}/`,
    `/api/groups/${rosterId}`,
  ];

  for (const url of tries) {
    try {
      const res = await api.get(url);
      if (!is2xx(res)) continue;

      const list =
        Array.isArray(res.data?.results) ? res.data.results :
        Array.isArray(res.data?.students) ? res.data.students :
        Array.isArray(res.data?.data) ? res.data.data :
        Array.isArray(res.data) ? res.data :
        [];

      if (list.length) return list;
    } catch {
      // try next
    }
  }

  // Fallback: helper that supports roster_id filtering and client-side handling
  try {
    const rep = await studentsApi.list({ roster_id: rosterId });
    if (rep?.ok) {
      const list = rep.students || rep.raw?.results || rep.raw || [];
      return Array.isArray(list) ? list : [];
    }
  } catch {
    // ignore; return []
  }

  return [];
}

// Normalize a student object to { id, name } for UI
function toStudentRow(s) {
  const id = s?.id ?? s?.scholar_id ?? s?.student_id;
  const parts = [
    s?.full_name,
    s?.name,
    [s?.first_name, s?.last_name].filter(Boolean).join(" "),
    [s?.firstName, s?.lastName].filter(Boolean).join(" "),
  ].filter(Boolean);
  const name = (parts[0] || "").trim() || `Student ${id ?? "?"}`;
  return { id: id ? String(id) : "", name };
}

export default function RosterSelectPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();

  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  // New state for the "after selection" flow
  const [selectedRoster, setSelectedRoster] = useState(null);
  const [studentRows, setStudentRows] = useState([]); // [{id, name}]
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [checked, setChecked] = useState(() => new Set()); // Set<string>

  useEffect(() => {
    let alive = true;
    (async () => {
      setBusy(true);
      setErr("");
      const res = await tryEndpoints();
      if (!alive) return;

      if (!res.ok) {
        console.error("Roster load attempts:", res.attempts);
        setErr("Unable to load rosters/groups. None of the expected endpoints responded (see console for attempts).");
        setRows([]);
      } else {
        setRows(res.list || []);
      }
      setBusy(false);
    })();
    return () => { alive = false; };
  }, []);

  // ----- checkbox helpers -----
  const allChecked = useMemo(
    () => studentRows.length > 0 && checked.size === studentRows.length,
    [studentRows, checked]
  );

  function toggleAll() {
    if (allChecked) {
      setChecked(new Set());
    } else {
      setChecked(new Set(studentRows.map((s) => s.id)));
    }
  }

  function toggleOne(id) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // ----- when a roster is clicked, fetch its students and show the list -----
  async function onSelect(roster) {
    setSelectedRoster(roster);
    setErr("");
    setStudentRows([]);
    setChecked(new Set());
    setLoadingStudents(true);

    try {
      const id = roster?.id ?? roster?.roster_id ?? roster?.group_id;
      const list = await fetchRosterStudentsRobust(id);
      const rows = (list || []).map(toStudentRow).filter((x) => x.id);
      setStudentRows(rows);
    } catch {
      setErr("Failed to load students for this roster.");
    } finally {
      setLoadingStudents(false);
    }
  }

  function onChangeRoster() {
    setSelectedRoster(null);
    setStudentRows([]);
    setChecked(new Set());
    setErr("");
  }

  function onContinue() {
    if (!selectedRoster) return;
    const next = new URLSearchParams(sp);

    const id = selectedRoster?.id ?? selectedRoster?.roster_id ?? selectedRoster?.group_id;
    const name = selectedRoster?.name;
    if (id) next.set("roster_id", String(id));
    if (name) next.set("roster_name", String(name));

    const studentIds = Array.from(checked);
    navigate(`/assign/reasons?${next.toString()}`, {
      state: { studentIds },
    });
  }

  return (
    <div style={s.page}>
      <h1 style={s.h1}>Select a Roster</h1>

      {/* Step 1: Roster grid */}
      {!selectedRoster && (
        <>
          {busy && <div>Loading…</div>}
          {err && <div style={s.error}>{err}</div>}

          {!busy && !err && (
            <div style={s.grid}>
              {rows.length === 0 && <div>No rosters found.</div>}
              {rows.map((r, idx) => (
                <button
                  key={r.id ?? r.roster_id ?? r.group_id ?? `${r.name ?? "roster"}-${idx}`}
                  onClick={() => onSelect(r)}
                  style={s.card}
                >
                  <div style={s.title}>{r.name || "Untitled Roster"}</div>
                  <div style={s.meta}>{r.description || " "}</div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Step 2: Students with checkboxes (including "Select All") */}
      {selectedRoster && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <h2 style={{ ...s.h2, margin: 0 }}>
              {selectedRoster?.name || "Roster"}
            </h2>
            <button onClick={onChangeRoster} type="button" style={s.linkBtn}>
              Change roster
            </button>
          </div>

          {loadingStudents && <div>Loading students…</div>}
          {err && <div style={s.error}>{err}</div>}

          {!loadingStudents && studentRows.length > 0 && (
            <div style={s.studentBox}>
              {/* Master checkbox */}
              <label style={{ ...s.row, borderBottom: "1px solid #eee", paddingBottom: 8 }}>
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={toggleAll}
                  style={{ marginRight: 8 }}
                />
                <strong>Select All</strong>
                <span style={{ marginLeft: "auto", color: "#666", fontSize: 12 }}>
                  {checked.size}/{studentRows.length} selected
                </span>
              </label>

              {/* Individual students */}
              <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
                {studentRows.map((s) => (
                  <label key={s.id} style={s.row}>
                    <input
                      type="checkbox"
                      checked={checked.has(s.id)}
                      onChange={() => toggleOne(s.id)}
                      style={{ marginRight: 8 }}
                    />
                    <span>{s.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {!loadingStudents && studentRows.length === 0 && !err && (
            <div>No students found in this roster.</div>
          )}

          {/* Actions */}
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <button
              type="button"
              style={s.btnPrimary}
              disabled={checked.size === 0 || loadingStudents}
              onClick={onContinue}
            >
              Continue ({checked.size} selected)
            </button>
            <button type="button" style={s.btnSecondary} onClick={onChangeRoster}>
              Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { padding: 20 },
  h1: { margin: "10px 0 8px", fontSize: 24, color: "#003e73", fontWeight: 800 },
  h2: { color: "#003e73", fontSize: 18, fontWeight: 700 },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: 12,
  },
  card: {
    textAlign: "left",
    padding: 14,
    borderRadius: 10,
    border: "1px solid rgba(0,0,0,0.1)",
    background: "#fff",
    cursor: "pointer",
  },
  title: { fontWeight: 700, color: "#003e73", marginBottom: 4 },
  meta: { color: "#666", marginTop: 6, fontSize: 13 },
  error: {
    color: "#b00020",
    background: "#FFEAEA",
    border: "1px solid #F5B5B5",
    borderRadius: 8,
    padding: "8px 10px",
  },
  studentBox: {
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: 10,
    background: "#fff",
  },
  row: {
    display: "flex",
    alignItems: "center",
    padding: "4px 2px",
  },
  btnPrimary: {
    height: 40,
    padding: "0 16px",
    border: "none",
    borderRadius: 8,
    background: "#003e73",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 600,
  },
  btnSecondary: {
    height: 40,
    padding: "0 16px",
    borderRadius: 8,
    border: "1px solid rgba(0,0,0,0.2)",
    background: "#fff",
    color: "#333",
    cursor: "pointer",
  },
  linkBtn: {
    border: "none",
    background: "transparent",
    color: "#0366d6",
    cursor: "pointer",
    textDecoration: "underline",
    padding: 0,
    height: 28,
    fontSize: 14,
  },
};
